import { round } from "lodash";
import { ObjectRideModeEnum } from "../objectRideHelpers";

export type MileageReportDistanceRow = {
  rideMode?: number;
  kilometersDriven?: number;
  privateMileage?: number;
  privateKilometersDriven?: number;
  businessKilometersDriven?: number;
};

export type FormattedReportDistance = {
  total: string | null;
  breakdown: string | null;
};

type DistanceTranslator = (
  key: string,
  options?: Record<string, string>
) => string;

export const formatKilometersDriven = (km: number): string =>
  `${round(km, 1).toString().replace(".", ",")} km`;

const formatKilometersValue = (km: number): string =>
  round(km, 1).toString().replace(".", ",");

export const getMixedRideDistanceBreakdown = (
  row: MileageReportDistanceRow
): { businessKm: number; privateKm: number } | null => {
  if (row.rideMode !== ObjectRideModeEnum.MIXED) {
    return null;
  }

  const privateKm = row.privateMileage ?? row.privateKilometersDriven ?? 0;
  if (!(privateKm > 0)) {
    return null;
  }

  const totalKm = row.kilometersDriven ?? 0;
  const businessKm =
    row.businessKilometersDriven ?? round(totalKm - privateKm, 1);

  return { businessKm, privateKm };
};

export const formatReportDistance = (
  row: MileageReportDistanceRow,
  translator?: DistanceTranslator
): FormattedReportDistance => {
  if (!row?.kilometersDriven) {
    return { total: null, breakdown: null };
  }

  const total = formatKilometersDriven(row.kilometersDriven);
  const split = getMixedRideDistanceBreakdown(row);

  if (!split || !translator) {
    return { total, breakdown: null };
  }

  const businessLabel = translator("typography.businessMileage");
  const privateLabel = translator("typography.privateMileage");
  const breakdown = translator("typography.mixedRideBreakdown", {
    business: formatKilometersValue(split.businessKm),
    private: formatKilometersValue(split.privateKm),
    businessLabel,
    privateLabel
  });

  return { total, breakdown };
};
